import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { scoped, useCircleChoice } from '/circlechoice.js';
import { cachedGet, peek } from '/read.js';
import { Icon } from '/icons.js';
import { Gauge } from '/shell.js';
import { Shortcuts } from '/shortcuts.js';

/**
 * The League tab — the screen in propool.jpeg.
 *
 * The dial, one big button, and then only things that are true: matches you
 * are in the middle of, matches waiting on somebody, and what you have just
 * played. The mockup's UPCOMING FIXTURES is gone, because Cueforge has no
 * fixtures — TO CONFIRM sits in that slot instead, which is real and is the
 * thing you actually need to be told about.
 */

/**
 * The rating and its recent movement, from the ledger the engine already keeps.
 *
 * Scoped to ONE circle. A rating is earned in a circle and a leaderboard ranks
 * a circle, so a single number across all of them agreed with no board on
 * screen — a circle where nothing had been played showed everybody at the
 * strength they had earned somewhere else.
 */
function useProgression(userId, circleId, noCircles) {
  // Held WITH the path it came from. Without that, switching circles kept the
  // previous circle's ledger on screen for a whole round trip, under a label
  // already naming the new one — the exact lie scoping these numbers was meant
  // to end. State is what to draw for the path being asked about; for any
  // other path it is somebody else's answer.
  const [held, setHeld] = useState({ path: null, rows: null });
  const path = userId
    ? `/api/players/${userId}/progression${scoped(circleId)}` : null;
  const setRows = (rows) => setHeld({ path, rows });
  const shown = held.path === path && held.rows !== null ? held.rows
    : (path && circleId ? peek(path) : undefined);

  useEffect(() => {
    if (!userId) return;
    // Empty ONLY when there are genuinely no circles. "No circle chosen yet"
    // is a different state and briefly true on every load, and treating the
    // two the same put 1500 on the dial for a moment before the real number
    // arrived — which this file already says is worse than showing none.
    if (noCircles) { setRows([]); return; }
    if (!circleId) return;
    // NOT cleared: `seen` above is the whole point, and blanking it here undid
    // the warm one line after using it — the dial went back to waiting for a
    // number it already had.
    cachedGet(path).then(setRows).catch(() => setRows([]));
    // noCircles belongs in the deps: without it the effect never re-ran when
    // the circles arrived, so rows stayed null and the dial never appeared.
  }, [userId, circleId, noCircles]);
  return shown === undefined ? null : shown;
}

function Result({ row }) {
  const [mine, theirs] = row.score || [0, 0];
  return html`
    <div class="card rowbetween" data-testid="recent-row">
      <div style="display:flex;align-items:center;gap:.6rem">
        <div class=${`result ${row.won ? 'w' : 'l'}`}>${row.won ? 'W' : 'L'}</div>
        <div>
          <div data-testid="recent-name">${row.opponent_name || 'Someone'}</div>
          <div class="sub mono" data-testid="recent-score">${`${mine}–${theirs}`}</div>
        </div>
      </div>
      <span class=${`mono ${row.delta >= 0 ? 'chalk' : 'dangerc'}`}>
        ${`${row.delta >= 0 ? '+' : ''}${Math.round(row.delta)}`}
      </span>
    </div>`;
}

export function LeagueTab({ authState, onGo }) {
  const [circles, circleId, chooseCircle] = useCircleChoice();
  const rows = useProgression(authState.user_id, circleId,
                              circles !== null && circles.length === 0);
  // Seeded from what is already known, not from empty. Starting empty meant
  // the match you have on APPEARED a render later and pushed everything under
  // it down — which is what "home flashes, it is due to an open match" is
  // from the outside. Empty is the right answer only when the answer is empty.
  const [pending, setPending] = useState(() => peek('/api/me/pending')
    || { to_confirm: [], awaiting_them: [], disputed: [], in_progress: [] });
  const [error, setError] = useState('');
  // Your rating in each league you field a team in (issue #24). A second
  // book, never merged with the dial above it: that one is this app's own
  // friendlies, this one is the league's records.
  const [leagueRatings, setLeagueRatings] = useState([]);

  const reload = async () => {
    try { setPending(await cachedGet('/api/me/pending')); }
    catch (e) { setError(e.detail || e.message); }
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    // Its own effect and failure-tolerant: a league rating is a nice thing
    // to see, and losing it must not take the home screen with it.
    cachedGet('/api/me/league-ratings')
      .then(setLeagueRatings)
      .catch(() => setLeagueRatings([]));
  }, []);

  const act = (id, what) => async () => {
    setError('');
    try { await post(`/api/matches/${id}/${what}`); await reload(); }
    catch (e) { setError(e.detail || e.message); }
  };

  const played = rows || [];
  const friendlies = played.length ? played[played.length - 1].after : 1500;
  // What the last match did, which is the number somebody recognises — not
  // the total since 1500, which only ever grows and says nothing about form.
  const friendliesDelta = played.length
    ? played[played.length - 1].delta : null;

  // Issue #24, second round. Once the selected circle has become a team in a
  // league, the LEAGUE's rating is the one that belongs on the dial: the
  // friendlies number is the previous life of a thing that has moved on, and
  // showing the two together made the stale one the prominent one.
  //
  // Keyed off the SELECTED circle, not merely "a league exists somewhere".
  // Somebody in a Thursday-night circle AND a league team holds two genuinely
  // different numbers, and the dial has to answer for the circle named
  // beneath it or that label lies about the figure above it.
  const dialLeague = leagueRatings.find(
    (l) => (l.circle_ids || []).includes(circleId)) || null;
  const rating = dialLeague ? dialLeague.elo : friendlies;
  // No delta for a league rating. `career_ratings` gives a standing and a
  // frame count, not what the last frame moved — and deriving one from the
  // friendlies book would put this app's arithmetic underneath the league's
  // number, which is the merge this screen exists to avoid.
  const delta = dialLeague ? null : friendliesDelta;
  // Whatever the dial is NOT showing. A league listed twice — once on the
  // face and once underneath — reads as two different ratings.
  const otherLeagues = leagueRatings.filter((l) => l !== dialLeague);
  const streak = (() => {
    let n = 0;
    for (let i = played.length - 1; i >= 0 && played[i].won; i -= 1) n += 1;
    return n;
  })();
  const score = (m) => `${m.mine}–${m.theirs}`;

  return html`
    ${/* No dial until the ledger has answered. Defaulting to 1500 while it
          loads puts a WRONG rating on screen — briefly, but it is the number
          the whole tab exists to show, and a flash of the wrong one is worse
          than a moment of none. */''}
    ${rows === null || circles === null
      ? html`<div class="gauge" data-testid="gauge-waiting"></div>`
      : html`<${Gauge} value=${rating} delta=${delta} />`}

    ${/* Which circle this number is for. Named even when there is only one,
          because an unlabelled rating is what made two circles look the
          same — and a picker when there are several, since they are now
          genuinely different numbers. */''}
    ${circles !== null && circles.length > 0 && html`
      ${circles.length === 1
        ? html`<label data-testid="gauge-circle-name"
                      style="text-align:center">${circles[0].name}</label>`
        : html`
          <div class="seg" style="justify-content:center"
               data-testid="gauge-circle">
            ${circles.map((c) => html`
              <button key=${c.id} aria-pressed=${c.id === circleId}
                      data-testid=${`gauge-circle-${c.id}`}
                      onClick=${() => chooseCircle(c.id)}>${c.name}</button>`)}
          </div>`}`}

    ${/* Which book the dial is showing. Only drawn when it is a league's,
          because "ELO RATING" over a number is ambiguous the moment there
          are two sources for it — and this is the one somebody would
          otherwise mistake for their friendlies rating. */''}
    ${dialLeague && html`
      <label data-testid="gauge-league" style="text-align:center">
        ${`${dialLeague.name} · ${dialLeague.games} `}
        ${dialLeague.games === 1 ? 'frame' : 'frames'}
      </label>`}

    ${/* The leagues the dial is NOT showing. Beside it, not inside it: the
          dial is one number and this may be several -- and they are
          different books, so stacking them in one face would say they were
          comparable. */''}
    ${otherLeagues.length > 0 && html`
      <div class="stack" data-testid="home-league-ratings">
        ${otherLeagues.map((l) => html`
          <div class="card rowbetween" key=${l.league_id}
               data-testid=${`home-league-rating-${l.league_id}`}>
            <span class="sub">${`${l.name} rating`}</span>
            <span class="mono pts">
              ${Math.round(l.elo)}
              <span class="sub" style="font-size:.8em">
                ${` · ${l.games} ${l.games === 1 ? 'frame' : 'frames'}`}
              </span>
            </span>
          </div>`)}
      </div>`}

    ${/* Pages somebody chose to keep -- issue #77. Below the ratings and
          above Start a match, where Ben asked for them: under the thing you
          came to look at, over the thing you came to do. */''}
    <${Shortcuts} onGo=${onGo} />

    <button class="felt" data-testid="home-record" onClick=${() => onGo('record')}>
      <${Icon} name="circle-dot" size=${18} />
      <span>Start a match</span>
    </button>

    ${(pending.in_progress || []).map((m) => html`
      <div class="card act" key=${m.match_id} data-testid="pending-live">
        <p class="sub" style="margin:0 0 .6rem">
          ${`Still playing ${m.opponent_name} — ${score(m)}`}
        </p>
        <button data-testid="pending-resume"
                onClick=${() => onGo('live', m.match_id)}>Back to the match</button>
      </div>`)}

    ${pending.to_confirm.length > 0 && html`
      <div class="stack">
        <label>To confirm</label>
        ${pending.to_confirm.map((m) => html`
          <div class="card act" key=${m.match_id}>
            <p class="sub" style="margin:0 0 .5rem">
              ${m.opponent_name} recorded
              <strong data-testid="pending-score">${score(m)}</strong>
              · ${m.game_format}
            </p>
            <div class="row">
              <button data-testid="pending-confirm"
                      onClick=${act(m.match_id, 'confirm')}>That is right</button>
              <button class="secondary" data-testid="pending-dispute"
                      onClick=${act(m.match_id, 'dispute')}>Dispute</button>
            </div>
          </div>`)}
      </div>`}

    ${(pending.disputed || []).length > 0 && html`
      <div class="stack">
        <label>Disputed</label>
        ${pending.disputed.map((m) => html`
          <div class="card warn" key=${m.match_id} data-testid="pending-disputed">
            <p class="sub" style="margin:0 0 .5rem">
              ${score(m)} v ${m.opponent_name} — someone says this is wrong, so
              it does not count. Discard it and record it again.
            </p>
            <button class="secondary" data-testid="pending-discard"
                    onClick=${act(m.match_id, 'void')}>Discard</button>
          </div>`)}
      </div>`}

    ${pending.awaiting_them.length > 0 && html`
      <div class="stack">
        <label>Waiting for them</label>
        ${pending.awaiting_them.map((m) => html`
          <div class="card" key=${m.match_id} data-testid="pending-awaiting">
            <p class="sub" style="margin:0">
              ${score(m)} v ${m.opponent_name} — not counted until they confirm.
            </p>
          </div>`)}
      </div>`}

    <div class="stack">
      <label>Recent matches</label>
      ${played.length === 0
        ? html`<p class="empty" data-testid="recent-empty">
                 Nothing counted yet. A match against somebody with an account
                 only counts once they confirm it.
               </p>`
        : played.slice().reverse().slice(0, 5).map((r, i) => html`
            <${Result} key=${i} row=${r} />`)}
    </div>

    ${streak > 1 && html`
      <div class="rowbetween mutedc mono" data-testid="home-streak"
           style="justify-content:center;gap:.4rem;font-size:.625rem">
        <${Icon} name="flame" size=${12} />
        <span>${`${streak}-match win streak`}</span>
      </div>`}

    ${/* Everything you have played, which until now was only ever visible as
          the last few results above. */''}
    <button class="secondary" data-testid="home-matches"
            onClick=${() => onGo('matches')}>Match history</button>

    <button class="secondary" data-testid="home-progress"
            onClick=${() => onGo('progress')}>Your rating over time</button>

    ${/* The leaderboard's way in. It was a quarter of the tab bar until
          Leagues — by then much the largest part of the app — needed the slot
          more. Here is the better home for it anyway: beside the other two
          "how am I doing" screens, and somewhere you go and come back from
          rather than one of the four places you live. Without this button the
          screen would still exist and be reachable by URL, which is the same
          as not existing. */''}
    <button class="secondary" data-testid="home-board"
            onClick=${() => onGo('board')}>Leaderboard</button>

    ${error && html`<p class="error" data-testid="error">${error}</p>`}`;
}
